import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import { invalidateAfterMutation } from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { adminEndpoints } from "@/features/admin/api/endpoints";
import type {
  AuditLogDto,
  SimulateAuditEventRequestDto,
} from "@/features/admin/api/dto";

/**
 * ⚙️ Enregistre un événement d'audit simulé
 * (`POST /AuditLogs/simulate-audit-event`) — point d'entrée de remplacement
 * du consommateur Kafka d'audit, non encore câblé. Déduplication par
 * `eventId`. Rôle : flashpay-superadmin uniquement (§6.8).
 */
export const useSimulateAuditEvent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["admin", "audit-logs", "simulate-audit-event"],
    mutationFn: (body: SimulateAuditEventRequestDto) =>
      mutateData({
        url: adminEndpoints.auditLogs.simulateAuditEvent,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<AuditLogDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, [["ADMIN_AUDIT_LOGS_LIST"]], {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSimulateAuditEvent failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "admin" }
      );
    },
  });
};
